import React, { useEffect, useMemo, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Button, Alert, Spinner, Badge } from '../components/ui';
import GroupAssignmentPicker from '../components/GroupAssignmentPicker';
import RichHtmlEditor from '../components/RichHtmlEditor';
import ContentCalendarView from '../components/ContentCalendarView';
import { findScheduleConflicts } from '../utils/contentCalendar';
import useOrganizationBrandColors from '../hooks/useOrganizationBrandColors';
import useOverlayClose from '../hooks/useOverlayClose';
import { useT } from '../i18n';
import './Disclaimers.css';

const messages = {
  docTitle: { tr: 'Yasal Uyarılar - Signature Manager', en: 'Disclaimers - Signature Manager' },
  emptyContent: { tr: 'İçerik boş olamaz', en: 'Content can’t be empty' },
  needGroup: { tr: 'En az bir grup seçmelisiniz.', en: 'Select at least one group.' },
  saveFailed: { tr: 'Kaydedilemedi: {msg}', en: 'Couldn’t save: {msg}' },
  confirmDelete: { tr: 'Bu yasal uyarıyı silmek istediğinize emin misiniz?', en: 'Are you sure you want to delete this disclaimer?' },
  deleteFailed: { tr: 'Silinemedi: {msg}', en: 'Couldn’t delete: {msg}' },
  title: { tr: 'Yasal Uyarılar', en: 'Disclaimers' },
  hint: {
    tr: 'Sektörünüze özel uyumluluk metinlerini oluşturun, hangi gruplara gösterileceğini seçin — imzanın altına otomatik olarak eklenir.',
    en: 'Create compliance text specific to your industry and choose which groups see it — it’s added below the signature automatically.',
  },
  newDisclaimer: { tr: '+ Yeni Yasal Uyarı', en: '+ New Disclaimer' },
  list: { tr: 'Liste', en: 'List' },
  calendar: { tr: 'Takvim', en: 'Calendar' },
  error: { tr: 'Hata: {msg}', en: 'Error: {msg}' },
  conflictWarning: {
    tr: 'Aynı grubu hedefleyen ve tarihleri çakışan yasal uyarılar var — takvimde turuncu işaretli. En son güncellenen kazanır, diğerleri o tarih aralığında gösterilmez.',
    en: 'Some disclaimers target the same group with overlapping dates — marked orange on the calendar. The most recently updated one wins; the others aren’t shown in that date range.',
  },
  editTitle: { tr: 'Yasal Uyarıyı Düzenle', en: 'Edit Disclaimer' },
  createTitle: { tr: 'Yeni Yasal Uyarı', en: 'New Disclaimer' },
  content: { tr: 'İçerik', en: 'Content' },
  dateRange: { tr: 'Yayın Tarihi Aralığı (opsiyonel)', en: 'Publishing Date Range (optional)' },
  dateRangeHint: {
    tr: 'Boş bırakılırsa uyarı süresiz aktif olur. Doldurulursa yalnızca bu aralıkta uygulanır — Takvim görünümünde gösterilir.',
    en: 'Leave empty to keep the disclaimer active indefinitely. If set, it only applies in this range — shown in the Calendar view.',
  },
  targetGroups: { tr: 'Hedef Gruplar', en: 'Target Groups' },
  targetGroupsHint: {
    tr: 'Bu yasal uyarının uygulanacağı grupları seçin. Hiçbir grup seçilmezse uyarı hiç kimseye gösterilmez.',
    en: 'Choose the groups this disclaimer applies to. If no group is selected, nobody sees it.',
  },
  colGroups: { tr: 'Gruplar', en: 'Groups' },
  colRange: { tr: 'Tarih Aralığı', en: 'Date Range' },
  colAction: { tr: 'Aksiyon', en: 'Actions' },
  empty: { tr: 'Henüz yasal uyarı eklenmedi.', en: 'No disclaimers added yet.' },
  conflict: { tr: 'Çakışma', en: 'Conflict' },
  groupCount: { tr: '{n} grup', en: ({ n }) => `${n} group${n === 1 ? '' : 's'}` },
  indefinite: { tr: 'Süresiz', en: 'Indefinite' },
};

const emptyForm = { name: '', html_content: '', start_date: '', end_date: '' };

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Disclaimers() {
  const overlayClose = useOverlayClose(() => closeForm());
  const { t, formatDate } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [brandColors] = useOrganizationBrandColors();

  const [disclaimers, setDisclaimers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [viewMode, setViewMode] = useState('list');

  const calendarItems = useMemo(() => disclaimers
    .filter(d => d.start_date || d.end_date)
    .map(d => ({
      id: d.id, name: d.name, groups: d.groups || [],
      start_date: d.start_date ? new Date(d.start_date) : null,
      end_date: d.end_date ? new Date(d.end_date) : null,
    })), [disclaimers]);
  const undatedItems = useMemo(() => disclaimers
    .filter(d => !d.start_date && !d.end_date)
    .map(d => ({ id: d.id, name: d.name })), [disclaimers]);
  const conflictIds = useMemo(() => findScheduleConflicts(calendarItems), [calendarItems]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.signatures.disclaimers.list);
      setDisclaimers(data.results || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const data = await api.get(endpoints.signatures.groups.visibility);
      setGroups(data.filter(g => g.visible).map(g => ({ id: g.group_name, name: g.group_name })));
    } catch (err) {
      setGroups([]);
    }
  };

  useEffect(() => { load(); loadGroups(); }, []);

  const toggleGroup = (id) => {
    setAssignments(prev => {
      const copy = { ...prev };
      if (copy[id] != null) delete copy[id];
      else copy[id] = 1;
      return copy;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setAssignments({});
    setError(null);
    setShowForm(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name, html_content: d.html_content,
      start_date: toDatetimeLocal(d.start_date), end_date: toDatetimeLocal(d.end_date),
    });
    setAssignments(Object.fromEntries((d.groups || []).map(g => [g, 1])));
    setError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setAssignments({});
    setError(null);
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleHtmlChange = (content) => {
    setForm(prev => ({ ...prev, html_content: content }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // The textarea this replaced had `required`, which no longer applies —
    // TinyMCE's editable surface is an iframe, not a native form control the
    // browser validates on its own.
    if (!form.html_content || form.html_content === '<p></p>') {
      setError(t('emptyContent'));
      return;
    }
    if (Object.keys(assignments).length === 0) {
      setError(t('needGroup'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
        group_names: Object.keys(assignments),
      };
      if (editing) {
        await api.put(endpoints.signatures.disclaimers.detail(editing.id), payload);
      } else {
        await api.post(endpoints.signatures.disclaimers.create, payload);
      }
      closeForm();
      await load();
    } catch (err) {
      const errMsg = err.response?.data
        ? Object.entries(err.response.data).map(([k, v]) => `${k}: ${v}`).join(' | ')
        : err.message;
      setError(t('saveFailed', { msg: errMsg }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    setError(null);
    try {
      await api.delete(endpoints.signatures.disclaimers.detail(id));
      await load();
    } catch (err) {
      setError(t('deleteFailed', { msg: err.message }));
    }
  };

  return (
    <div className="page-stack">
      <h1>{t('title')}</h1>
      <p className="disclaimers-hint">{t('hint')}</p>
      <div className="disclaimers-toolbar">
        <Button onClick={openCreate}>{t('newDisclaimer')}</Button>
        <div className="disclaimers-view-toggle">
          <Button variant={viewMode === 'list' ? 'primary' : 'secondary'} onClick={() => setViewMode('list')}>{t('list')}</Button>
          <Button variant={viewMode === 'calendar' ? 'primary' : 'secondary'} onClick={() => setViewMode('calendar')}>{t('calendar')}</Button>
        </div>
      </div>

      {error && !showForm && <Alert variant="error">{t('error', { msg: error })}</Alert>}
      {viewMode === 'calendar' && conflictIds.size > 0 && (
        <Alert variant="warning">
          {t('conflictWarning')}
        </Alert>
      )}

      {showForm && (
        <div className="modal-overlay" {...overlayClose}>
          <div className="modal-window disclaimer-modal">
            <form onSubmit={handleSubmit} className="disclaimer-form">
              <h2>{editing ? t('editTitle') : t('createTitle')}</h2>
              {/* Rendered inside the modal (not just the page-level Alert
                  above) so a submit failure — including the empty-content
                  check below, which used to be a native browser tooltip on
                  the old required textarea — is actually visible instead of
                  sitting behind the modal overlay. */}
              {error && <Alert variant="error">{error}</Alert>}
              <div className="form-group">
                <label>{t('name')}</label>
                <input
                  type="text" name="name" value={form.name} onChange={handleFieldChange}
                  required className="disclaimer-name-input"
                />
              </div>
              <div className="form-group">
                <label>{t('content')}</label>
                <RichHtmlEditor
                  key={editing ? `edit-${editing.id}` : 'new'}
                  initialValue={form.html_content}
                  onEditorChange={handleHtmlChange}
                  height={360}
                  brandColors={brandColors}
                />
              </div>
              <div className="form-group">
                <label>{t('dateRange')}</label>
                <p className="form-group-hint">{t('dateRangeHint')}</p>
                <div className="disclaimer-form-dates">
                  <input type="datetime-local" name="start_date" value={form.start_date} onChange={handleFieldChange} />
                  <input type="datetime-local" name="end_date" value={form.end_date} onChange={handleFieldChange} />
                </div>
              </div>
              <div className="form-group">
                <label>{t('targetGroups')}</label>
                <p className="form-group-hint">{t('targetGroupsHint')}</p>
                <GroupAssignmentPicker groups={groups} assignments={assignments} onToggleGroup={toggleGroup} />
              </div>
              <div className="form-actions">
                <Button type="submit" disabled={saving}>{saving ? t('saving') : t('save')}</Button>
                <Button type="button" variant="secondary" onClick={closeForm}>{t('cancel')}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : viewMode === 'calendar' ? (
        <ContentCalendarView
          items={calendarItems}
          conflictIds={conflictIds}
          undatedItems={undatedItems}
          onSelectItem={(item) => {
            const d = disclaimers.find(x => x.id === item.id);
            if (d) openEdit(d);
          }}
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('name')}</th>
              <th>{t('colGroups')}</th>
              <th>{t('colRange')}</th>
              <th>{t('status')}</th>
              <th>{t('colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {disclaimers.length === 0 && (
              <tr><td colSpan={5} className="data-table-empty">{t('empty')}</td></tr>
            )}
            {disclaimers.map(d => (
              <tr key={d.id}>
                <td>
                  {d.name}
                  {conflictIds.has(d.id) && <Badge variant="warning" className="disclaimer-conflict-badge">{t('conflict')}</Badge>}
                </td>
                <td>{t('groupCount', { n: (d.groups || []).length })}</td>
                <td>
                  {d.start_date || d.end_date
                    ? `${d.start_date ? formatDate(d.start_date) : '—'} – ${d.end_date ? formatDate(d.end_date) : '—'}`
                    : t('indefinite')}
                </td>
                <td><Badge variant={d.is_active ? 'success' : 'danger'}>{d.is_active ? t('active') : t('inactive')}</Badge></td>
                <td className="disclaimer-actions">
                  <Button variant="secondary" onClick={() => openEdit(d)}>{t('edit')}</Button>
                  <Button variant="danger" onClick={() => handleDelete(d.id)}>{t('delete')}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
